import { SetStateAction, useContext, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  LinearProgress,
  TablePagination,
  Typography,
} from '@mui/material';
import { ClearIcon } from '@mui/x-date-pickers';
import axios, { AxiosResponse } from 'axios';
import { isMobile, isTablet, isDesktop } from 'react-device-detect';
import { API_ENDPOINT } from '../../../configurations/global.config';
import { PaginationContext } from '../../../contexts/pagination-context';
import { useSnackBar } from '../../../contexts/snackbar';
import { AuthContext } from '../../../contexts/user-context/user-context';
import { PickupTaskState } from '../../../enums/tasks';
import { IPickupTask } from '../../../interfaces/delivery-company/tasks';
import ConfirmAction from '../../common/confirm-action/confirm-action';
import CustomDialog from '../../common/custom-dialog/custom-dialog';
import DoubleDatePicker from '../../common/double-date-picker/double-date-picker';
import { DoubleDate } from '../../common/double-date-picker/double-date-picker.interfaces';
import { ErrorsFlattenner } from '../../common/errors-flattenner/errors-flattenner';
import FilterBar from '../../common/filter-bar/filter-bar';
import { buildQueryParams } from '../../common/filter-bar/filter-bar.utils';
import { IFilterOption } from '../../common/filter-bar/types';
import ResponsivePickupTasksListing from '../common/pickup-tasks-listing';
import MerchantCreatePickupRequest from './merchant-create-task-form/merchant-create-task-form';
import PickupTaskDialog from './merchant-tasks-listing/pickup-task-dialog';
import { Workflow } from '../../../enums/orders';
import { PDFDownloadLink } from '@react-pdf/renderer';
import { DownloadIcon } from 'lucide-react';
import settings from '../../delivery-company-components/settings/theme-and-notifications';
import { OrderType } from '../../../interfaces/delivery-company/orders';
import { OrgSettingsContext } from '../../../contexts/org-settings-context';
import NewFeatureChip from '../../common/new-feature-chip';
import { PrintWaybillsMultiSizes } from '../../common/bulk-action/print-waybills';
import PickupTaskWaybillsDialog from '../../common/pickup-task-waybills-dialog';

function MerchantTasks() {
  const [tasks, setTasks] = useState<IPickupTask[]>([]);
  const [tasksCount, setTasksCount] = useState(0);
  const [editingTask, setEditingTask] = useState<IPickupTask | null>(null);

  const [printingWaybills, setPrintingWaybills] = useState<{pickup_id: number, orders: OrderType[]}>()
  const { settings} = useContext(OrgSettingsContext)

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [showWaybillsDialogFor, setShowWaybillsDialogFor] = useState<IPickupTask | null>(null);
  const [cancellingPickupRequest, setCancellingPickupRequest] =
    useState<IPickupTask>();
  const [response, setResponse] = useState<AxiosResponse>();
  const [message, setMessage] = useState<string>('');
  const [filters, setFilters] = useState<IFilterOption[]>([]);
  const [dateFilter, setDateFilter] = useState<DoubleDate>({
    startDate: null,
    endDate: null,
  });
  const [creatingNewTask, setCreatingNewTask] = useState(false);
  const { showMessage } = useSnackBar();

  const { user, config } = useContext(AuthContext);

  const { paginationConfig, setPaginationConfig } =
    useContext(PaginationContext);

  const [controller, setController] = useState({
    page: 0,
    rowsPerPage: paginationConfig.rowsPerPage,
  });

   const fetchTasks = async () => {
    setIsLoading(true);
      const relevant_fields = [
        'id',
        'status',
        'package_count',
        'workflow',
        'creation_date',
        'created_by',
        'merchant',
        'customer',
        'customer_location',
        'merchant_location',
      ];

      let endpoint = `${API_ENDPOINT}/api/v1/pickup-tasks/?page=${controller.page + 1}&page_size=${controller.rowsPerPage}&fields=${relevant_fields.join(',')}`;
      const filterQueryParams = buildQueryParams(filters);
      endpoint += `${filterQueryParams}`;

      const { startDate, endDate } = dateFilter;

      if (startDate !== null) {
        endpoint += `&date_from=${encodeURIComponent(startDate.format('YYYY-MM-DD'))}`;
      }

      if (endDate !== null) {
        endpoint += `&date_to=${encodeURIComponent(endDate.format('YYYY-MM-DD'))}`;
      }

      try {
        console.log(endpoint);
        const response = await axios.get<{
          count: number;
          results: IPickupTask[];
        }>(endpoint, config);
        console.log(response);
        setTasks(response.data.results);
        setTasksCount(response.data.count);
      } catch (error) {
        console.log(error);
      }
      setIsLoading(false);
    };
    
  useEffect(() => {
    fetchTasks();
  }, [JSON.stringify(controller), JSON.stringify(filters), dateFilter]);

  const handleCancelTask = async () => {
    try {
      setMessage('');
      setResponse(undefined);
      setIsLoading(true);
      const endpoint = `${API_ENDPOINT}/api/v1/pickup-task/${cancellingPickupRequest?.id}/`;
      const response = await axios.delete(endpoint, config);
      setResponse(response);
      setTasks(
        tasks.map((task) =>
          task.id === cancellingPickupRequest?.id
            ? { ...task, status: PickupTaskState.CANCELLED }
            : task
        )
      );
    } catch (e) {
      if (axios.isAxiosError(e)) {
        setResponse(e.response);
        setMessage(
          'Failed to cancel pickup request: ' + e.response?.statusText
        );
      } else {
        setMessage('Failed to cancel pickup request');
      }
    }
    setIsLoading(false);
  };

  const filterQueryAction = (filters: IFilterOption[]) => {
    resetPaginationControls();
    setFilters(filters);
  };

  const dateFilterAction = (dateFilterValue: DoubleDate) => {
    resetPaginationControls();
    setDateFilter(dateFilterValue);
  };

  const handlePageChange = (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent> | null,
    newPage: number
  ) => {
    setController({
      ...controller,
      page: newPage,
    });
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setPaginationConfig({ ...paginationConfig, rowsPerPage: newRowsPerPage });
    setController({
      ...controller,
      rowsPerPage: newRowsPerPage,
      page: 0,
    });
  };

  const searchQueryAction = (
    column: string,
    value: string,
    operation: string
  ) => {
    setController({
      ...controller,
      page: 0,
    });
  };

  const resetPaginationControls = () => {
    setController({
      ...controller,
      page: 0,
    });
  };

  const resetAll = () => {
    resetPaginationControls();
  };

  return (
    <div>
      <Box>
        <Typography
          variant="h3"
          component="h3"
          sx={{ textAlign: 'center', mt: 3, mb: 3 }}
        >
          Pickup Requests
        </Typography>


        <br />

        {
          <Grid container spacing={2}>
            <Grid
              xs={12}
              sx={{
                textAlign: 'right',
                display: 'flex',
                justifyContent: 'flex-end',
                gap: 1,
              }}
            >
              <Button
                size={(isMobile && !isTablet) ? 'small' : 'medium'}
                onClick={() => setCreatingNewTask(true)}
                variant="contained"
              >
                New Pickup Request
              </Button>
            </Grid>
            <Grid xs={12}>
              <Box>
                <DoubleDatePicker dateFilterAction={dateFilterAction} />
                <br />

                <FilterBar
                  filterQueryAction={filterQueryAction}
                  currentlySelectedFilters={filters}
                  filterKeys={['pickup task state', 'workflow']}
                />

                {isLoading ? (
                  <LinearProgress />
                ) : (
                  <>
                    {message != '' && (
                      <>
                        <Alert
                          severity={
                            response &&
                            response.status >= 200 &&
                            response.status < 300
                              ? 'success'
                              : 'error'
                          }
                        >
                          <>
                            {message}

                            {response && response.status >= 300 && (
                              <ErrorsFlattenner rawErrors={response.data} />
                            )}
                          </>
                        </Alert>
                        <br />{' '}
                      </>
                    )}

                    <ResponsivePickupTasksListing
                      tasksList={tasks}
                      isLoading={isLoading}
                      setCancellingPickupRequest={setCancellingPickupRequest}
                      setDownloadWaybillForTask={(task: IPickupTask) => {
                        setShowWaybillsDialogFor(task);
                      }}
                      setEditingTask={setEditingTask}
                    />

                    <TablePagination
                      component="div"
                      onPageChange={handlePageChange}
                      page={controller.page}
                      count={tasksCount}
                      rowsPerPage={controller.rowsPerPage}
                      onRowsPerPageChange={handleChangeRowsPerPage}
                      rowsPerPageOptions={[
                        5, 10, 25, 50, 100, 200, 250, 500, 1000,
                      ]}
                      labelRowsPerPage=""
                      style={{ overflow: 'visible' }}
                    />

                    {showWaybillsDialogFor && (
                      <PickupTaskWaybillsDialog
                        pickupTaskId={showWaybillsDialogFor.id}
                        onClose={() => setShowWaybillsDialogFor(null)}
                      />
                    )}
                    {editingTask && (
                      <PickupTaskDialog
                        taskID={editingTask.id}
                        onClose={() => setEditingTask(null)}
                      />
                    )}
                    <ConfirmAction
                      message={'Are you sure you want to cancel this task?'}
                      action={handleCancelTask}
                      isOpen={cancellingPickupRequest != undefined}
                      handleClose={() => setCancellingPickupRequest(undefined)}
                      isSevereAction={true}
                      confirmSevereActionTarget={'cancel request'}
                    >
                      <Alert severity="warning">
                        This action can not be undone.
                      </Alert>
                    </ConfirmAction>
                  </>
                )}
              </Box>
            </Grid>
          </Grid>
        }
      </Box>

      <CustomDialog
        open={creatingNewTask}
        fullWidth
        maxWidth="xl"
        fullScreen={(isMobile && !isTablet)}
        onClose={() => {
          setCreatingNewTask(false);
        }}
      >
        <div
          style={{
            textAlign: 'right',
            width: '100%',
            display: 'flex',
            justifyContent: 'flex-end',
          }}
        >
          <IconButton
            onClick={() => {
              setCreatingNewTask(false);
            }}
          >
            <ClearIcon color="primary" fontSize="large" />
          </IconButton>
        </div>

        <DialogContent
          style={(isDesktop || isTablet) ? { width: '100%', height: '100%' } : {}}
        >
          <MerchantCreatePickupRequest
            onSuccess={(p, orders, is_newly_created) => {
              showMessage(`Updated pickup tasks`);
              fetchTasks()
              if (p.workflow == Workflow.STANDARD) 
                setPrintingWaybills({
                  pickup_id: parseInt(p.id),
                  orders: orders
                });
              setCreatingNewTask(false);
            }}
          />
        </DialogContent>
      </CustomDialog>

      {printingWaybills && (
        <CustomDialog
          open={true}
          onClose={() => setPrintingWaybills(undefined)}
          maxWidth="md"
        >
          <div
            style={{
              textAlign: 'right',
              width: '100%',
              display: 'flex',
              justifyContent: 'space-between',
            }}
          >
            <DialogTitle>Print Packages Waybills </DialogTitle>
            <IconButton onClick={() => setPrintingWaybills(undefined)}>
              <ClearIcon color="primary" fontSize="large" />
            </IconButton>
          </div>
            <PrintWaybillsMultiSizes orders={printingWaybills.orders} onClose={()=>setPrintingWaybills(undefined)} />
        </CustomDialog>
      )}
    </div>
  );
}

export default MerchantTasks;
